/**
 * Silnik obliczeń dla modułu „Harmonogramy":
 *   - `parsePredecessors`  — parsuje tekst z kolumny Poprzednik (MS Project-style).
 *   - `resolveDependencies` — liczy start_date/end_date zadań na podstawie poprzedników.
 *   - `computeSummaryRollups` — dla zadań-sekcji (summary) oblicza rollupy z dzieci:
 *       start = min(start dzieci), end = max(end dzieci),
 *       days = (end - start) + 1,
 *       value = suma wartości dzieci (tylko liści),
 *       progress = ważona średnia (po dniach) progresu dzieci.
 *
 * Zadanie jest „summary" wtedy, gdy kolejne zadania w liście mają wyższy outline_level.
 * Reguły — dzieci to wszystkie zadania następujące po rodzicu aż do pierwszego zadania
 * o outline_level <= level(rodzica).
 */

import type { ScheduleTask } from '../../services/schedulesService';

export type DependencyType = 'FS' | 'SS' | 'FF' | 'SF';

export interface ParsedPredecessor {
  ordinal: number;
  type: DependencyType;
  lag: number;
  raw: string;
}

/** Parsuje ciąg poprzedników: "2", "2FS+5", "3SS", "4FF-2" (rozdzielone `,` lub `;`). */
export function parsePredecessors(text: string | null | undefined): ParsedPredecessor[] {
  if (!text) return [];
  const parts = String(text)
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter(Boolean);
  const out: ParsedPredecessor[] = [];
  const re = /^(\d+)\s*(FS|SS|FF|SF)?\s*([+-]\s*\d+)?$/i;
  for (const p of parts) {
    const m = re.exec(p.replace(/\s+/g, ''));
    if (!m) continue;
    const ordinal = parseInt(m[1], 10);
    const type = (m[2] || 'FS').toUpperCase() as DependencyType;
    const lag = m[3] ? parseInt(m[3].replace(/\s+/g, ''), 10) : 0;
    if (!Number.isFinite(ordinal)) continue;
    out.push({ ordinal, type, lag: Number.isFinite(lag) ? lag : 0, raw: p });
  }
  return out;
}

function toDate(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number): string {
  const d = toDate(iso);
  d.setDate(d.getDate() + Number(days || 0));
  return toIso(d);
}

/**
 * Przelicza start_date / end_date wszystkich zadań, które mają ustawiony poprzednik.
 * Iteracyjnie, żeby propagować zmiany wgłąb zależności (max 50 iteracji — zabezpieczenie).
 * Pomija zadania-summary (outline_level < następnego), żeby nie nadpisywać rollupów.
 */
export function resolveDependencies(tasks: ScheduleTask[]): ScheduleTask[] {
  if (!tasks.length) return tasks;
  const byOrdinal = new Map<number, ScheduleTask>();
  for (const t of tasks) byOrdinal.set(t.ordinal, t);

  const isSummary = computeSummaryFlags(tasks);
  const byId = new Map<number, boolean>();
  tasks.forEach((t, i) => byId.set(t.id, isSummary[i]));

  let working = tasks.map((t) => ({ ...t }));

  for (let iter = 0; iter < 50; iter++) {
    let changed = false;
    for (const t of working) {
      if (byId.get(t.id)) continue; /* summary liczymy osobno */
      const preds = parsePredecessors(t.predecessor);
      if (!preds.length) continue;
      /* Dla każdego poprzednika liczymy kandydata start_date; zadanie zaczyna się najpóźniej. */
      let bestStart: string | null = null;
      let bestEnd: string | null = null;
      for (const p of preds) {
        const src = working.find((w) => w.ordinal === p.ordinal);
        if (!src) continue;
        const days = Math.max(1, Number(t.days) || 1);
        let startIso: string;
        let endIso: string;
        if (p.type === 'FS') {
          startIso = addDaysIso(src.end_date, 1 + p.lag);
          endIso = addDaysIso(startIso, days - 1);
        } else if (p.type === 'SS') {
          startIso = addDaysIso(src.start_date, p.lag);
          endIso = addDaysIso(startIso, days - 1);
        } else if (p.type === 'FF') {
          endIso = addDaysIso(src.end_date, p.lag);
          startIso = addDaysIso(endIso, -(days - 1));
        } else {
          endIso = addDaysIso(src.start_date, p.lag);
          startIso = addDaysIso(endIso, -(days - 1));
        }
        if (!bestStart || startIso > bestStart) {
          bestStart = startIso;
          bestEnd = endIso;
        }
      }
      if (bestStart && bestEnd && (bestStart !== t.start_date || bestEnd !== t.end_date)) {
        t.start_date = bestStart;
        t.end_date = bestEnd;
        changed = true;
      }
    }
    if (!changed) break;
  }

  return working;
}

/**
 * Zwraca tablicę flag: `true` gdy zadanie na pozycji i jest summary (ma dzieci).
 * Zadanie jest summary, gdy istnieje kolejne zadanie o wyższym outline_level zanim
 * trafimy na zadanie o <= jego poziomu.
 */
export function computeSummaryFlags(tasks: ScheduleTask[]): boolean[] {
  const flags = new Array(tasks.length).fill(false);
  for (let i = 0; i < tasks.length; i++) {
    const level = tasks[i].outline_level || 0;
    for (let j = i + 1; j < tasks.length; j++) {
      const nl = tasks[j].outline_level || 0;
      if (nl <= level) break;
      if (nl > level) {
        flags[i] = true;
        break;
      }
    }
  }
  return flags;
}

export interface TaskRollup {
  isSummary: boolean;
  days: number;
  start_date: string;
  end_date: string;
  value: number;
  progress_pct: number;
}

/**
 * Zwraca mapę id → rollup (dla zadań-summary zawiera wyliczone wartości; dla liści
 * — wartości ze samego zadania). Wygodne do renderowania zarówno tabeli jak i Gantta.
 */
export function computeSummaryRollups(tasks: ScheduleTask[]): Map<number, TaskRollup> {
  const flags = computeSummaryFlags(tasks);
  const out = new Map<number, TaskRollup>();

  for (let i = 0; i < tasks.length; i++) {
    const t = tasks[i];
    if (!flags[i]) {
      out.set(t.id, {
        isSummary: false,
        days: Number(t.days) || 1,
        start_date: t.start_date,
        end_date: t.end_date,
        value: Number(t.value) || 0,
        progress_pct: Number(t.progress_pct) || 0,
      });
      continue;
    }
    /* Summary: szukamy dzieci (wszystkie zadania o poziomie > level aż do konca grupy). */
    const level = t.outline_level || 0;
    let minStart: string | null = null;
    let maxEnd: string | null = null;
    let sumValue = 0;
    let weightedProgressNumerator = 0;
    let weightedProgressDenominator = 0;

    for (let j = i + 1; j < tasks.length; j++) {
      const ch = tasks[j];
      const chLevel = ch.outline_level || 0;
      if (chLevel <= level) break;
      /* Tylko bezpośrednie dzieci ORAZ liście — wartości summary dziecka wyliczą się z ich dzieci,
       * a my i tak sumujemy tylko liście, bo tylko one mają niepuste `value/progress`. */
      const childIsSummary = flags[j];
      if (!childIsSummary) {
        if (!minStart || ch.start_date < minStart) minStart = ch.start_date;
        if (!maxEnd || ch.end_date > maxEnd) maxEnd = ch.end_date;
        sumValue += Number(ch.value) || 0;
        const d = Math.max(1, Number(ch.days) || 1);
        weightedProgressNumerator += d * (Number(ch.progress_pct) || 0);
        weightedProgressDenominator += d;
      }
    }

    const start_date = minStart || t.start_date;
    const end_date = maxEnd || t.end_date;
    const days =
      Math.round(
        (toDate(end_date).getTime() - toDate(start_date).getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

    out.set(t.id, {
      isSummary: true,
      days: Math.max(1, days),
      start_date,
      end_date,
      value: sumValue,
      progress_pct: weightedProgressDenominator
        ? Math.round((weightedProgressNumerator / weightedProgressDenominator) * 10) / 10
        : 0,
    });
  }

  return out;
}

/**
 * Normalizuje outline_level:
 *   - pierwsze zadanie ma level 0
 *   - każde kolejne może wzrosnąć o max 1 względem poprzedniego (żeby nie powstała „dziura")
 *   - wartości ujemne → 0, > 10 → 10
 */
export function normalizeOutline(tasks: ScheduleTask[]): ScheduleTask[] {
  if (!tasks.length) return tasks;
  const out = tasks.map((t) => ({ ...t }));
  out[0].outline_level = 0;
  for (let i = 1; i < out.length; i++) {
    const prev = out[i - 1].outline_level || 0;
    let lvl = Math.max(0, Math.min(10, Number(out[i].outline_level) || 0));
    if (lvl > prev + 1) lvl = prev + 1;
    out[i].outline_level = lvl;
  }
  return out;
}
